import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import './BalanceTopup.css';

const apiBase = '';

export default function BalanceTopup() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const paid = searchParams.get('paid');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [paying, setPaying] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const loadSession = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError('Geen geldige betaallink. Open de link uit uw e-mail of brief.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/balance-topup/session?t=${encodeURIComponent(token)}`);
      const json = await res.json();
      if (!json.success) {
        setError(json.error || 'Kon sessie niet laden');
        setSession(null);
      } else {
        setSession(json.data);
      }
    } catch (e) {
      setError('Netwerkfout. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (paid === '1') return;
    loadSession();
  }, [loadSession, paid]);

  // After Mollie redirect: finalize Magento credit if webhook was missed.
  useEffect(() => {
    if (paid !== '1' || !token) {
      if (paid === '1' && !token) {
        setConfirmState({ status: 'paid_unknown' });
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/balance-topup/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const json = await res.json();
        if (cancelled) return;
        if (!json.success) {
          setConfirmState({ status: 'error', error: json.error || 'Bevestigen mislukt' });
          return;
        }
        setConfirmState({
          status: json.data?.status || 'paid',
          magentoCredited: Boolean(json.data?.magentoCredited),
          alreadyDone: Boolean(json.data?.alreadyDone),
        });
      } catch (e) {
        if (!cancelled) setConfirmState({ status: 'error', error: 'Netwerkfout bij bevestigen' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paid, token]);

  const startPayment = async () => {
    if (!token) return;
    setPaying(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/balance-topup/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const json = await res.json();
      if (!json.success || !json.data?.checkoutUrl) {
        setError(json.error || 'Betaling starten mislukt');
        return;
      }
      window.location.href = json.data.checkoutUrl;
    } catch (e) {
      setError('Netwerkfout bij starten betaling.');
    } finally {
      setPaying(false);
    }
  };

  if (paid === '1') {
    const confirming = !confirmState;
    const ok =
      confirmState &&
      (confirmState.status === 'paid' ||
        confirmState.magentoCredited ||
        confirmState.alreadyDone ||
        confirmState.status === 'paid_unknown');
    return (
      <div className="balance-topup-page">
        <div className="balance-topup-card">
          <h1>{confirming ? 'Betaling controleren…' : ok ? 'Betaling ontvangen' : 'Betaling voltooid?'}</h1>
          {confirming ? (
            <p className="balance-topup-muted">Even geduld, we boeken uw saldo bij.</p>
          ) : ok ? (
            <p>
              Bedankt. Uw betaling is verwerkt{confirmState.magentoCredited ? ' en op uw saldo geboekt' : ''}.
            </p>
          ) : (
            <p>
              Als uw betaling bij Mollie is afgerond, wordt uw saldo zo snel mogelijk verwerkt in onze
              administratie. {confirmState?.error ? `(${confirmState.error})` : null}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="balance-topup-page">
      <div className="balance-topup-card">
        <h1>SDeal — saldo bijstorten</h1>
        {loading && <p className="balance-topup-muted">Laden…</p>}
        {error && <p className="balance-topup-error">{error}</p>}
        {session && !error && (
          <>
            <p>
              <span className="balance-topup-muted">Ten name van</span>
              <br />
              <strong>{session.recipientLabel}</strong>
            </p>
            <p className="balance-topup-amount">
              € {session.amountEuro} <span className="balance-topup-currency">{session.currency}</span>
            </p>
            <p className="balance-topup-muted balance-topup-expiry">
              Deze link is geldig t/m {new Date(session.expiresAt).toLocaleDateString('nl-NL')}.
            </p>
            <button
              type="button"
              className="balance-topup-pay-btn"
              onClick={startPayment}
              disabled={paying}
            >
              {paying ? 'Bezig…' : 'Veilig betalen via Mollie'}
            </button>
            <p className="balance-topup-footnote">
              U wordt doorgestuurd naar de betaalpagina van onze payment provider. Wij slaan geen
              kaartgegevens op.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
